import { createC2pa, type ManifestStore } from '@contentauth/c2pa-web';
import wasmSrc from '@contentauth/c2pa-web/resources/c2pa.wasm?url';
import workerSrc from '@contentauth/c2pa-web/c2pa_worker?url';
import type { VerificationResult } from '@upl/evidence-schema';
import {
  C2PA_ADAPTER_DESCRIPTOR,
  createAdapterExecutionResult,
  verificationResultToEvidencePatch,
  type AdapterExecutionResult,
  type EvidenceAdapter,
  type EvidenceAdapterContext,
} from '@upl/evidence-adapter-runtime';
import type { AdapterContext, ArtifactInput } from '@upl/provenance-core';
import { normalizeC2paEvidence, normalizeC2paEvidenceFailure, type C2paReadResult } from './normalize.js';

export interface C2paNativeReader {
  read(bytes: Uint8Array, mediaType: string): Promise<C2paReadResult>;
  dispose?(): void;
}

export function createOfficialC2paReader(): C2paNativeReader {
  let sdkPromise: ReturnType<typeof createC2pa> | undefined;
  const getSdk = (): ReturnType<typeof createC2pa> => {
    sdkPromise ??= globalThis.location.protocol === 'https:'
      ? createC2pa({ wasmSrc, workerSrc: new URL(workerSrc, globalThis.location.href) })
      : createC2pa({ wasmSrc });
    return sdkPromise;
  };

  return {
    async read(bytes, mediaType): Promise<ManifestStore | null> {
      const sdk = await getSdk();
      const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      const reader = await sdk.reader.fromBlob(mediaType, new Blob([buffer], { type: mediaType }));
      if (!reader) return null;
      try {
        return await reader.manifestStore();
      } finally {
        await reader.free();
      }
    },
    dispose(): void {
      void sdkPromise?.then((sdk) => sdk.dispose());
      sdkPromise = undefined;
    },
  };
}

export class C2PAAdapter implements EvidenceAdapter {
  readonly descriptor = C2PA_ADAPTER_DESCRIPTOR;
  readonly name = 'C2PAAdapter';
  readonly version = '0.13.4';
  readonly capabilities = ['C2PA_JPEG_LOCAL', 'C2PA_PNG_LOCAL'] as const;

  constructor(private readonly reader: C2paNativeReader = createOfficialC2paReader()) {}

  supports(input: ArtifactInput | EvidenceAdapterContext): boolean {
    const artifact = 'artifact' in input ? input.artifact : input;
    return artifact.detectedMediaType === 'image/jpeg' || artifact.detectedMediaType === 'image/png';
  }

  async inspect(context: EvidenceAdapterContext): Promise<AdapterExecutionResult> {
    const selectedAt = new Date().toISOString();
    const started = performance.now();
    const canonical = await this.verify(context.artifact, {
      verificationId: context.verificationId,
      startedAt: context.startedAt,
      ...(context.signal ? { signal: context.signal } : {}),
    });
    const completedAt = new Date().toISOString();
    const failed = canonical.processing.errors.length > 0;
    return createAdapterExecutionResult({
      descriptor: this.descriptor,
      state: failed ? 'FAILED' : 'COMPLETED',
      selectedAt,
      startedAt: selectedAt,
      completedAt,
      durationMs: performance.now() - started,
      method: '@contentauth/c2pa-web',
      ...(failed ? {} : { evidence: verificationResultToEvidencePatch(canonical, this.descriptor, completedAt, '@contentauth/c2pa-web') }),
      warnings: failed ? canonical.warnings : [],
      limitations: failed ? canonical.limitations : [],
      sourceMetadata: canonical.raw_provider_evidence?.c2pa && typeof canonical.raw_provider_evidence.c2pa === 'object'
        ? { c2pa: canonical.raw_provider_evidence.c2pa }
        : {},
      processingMetadata: { display_name: '@contentauth/c2pa-web', display_version: this.version },
      errors: canonical.processing.errors.map(({ code }) => ({ code })),
    });
  }

  async verify(input: ArtifactInput, context: AdapterContext): Promise<VerificationResult> {
    const started = performance.now();
    try {
      const store = await this.reader.read(input.bytes, input.detectedMediaType);
      return await normalizeC2paEvidence(input, context, store, performance.now() - started);
    } catch {
      return await normalizeC2paEvidenceFailure(input, context, performance.now() - started);
    }
  }
}
